import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { dequal } from 'dequal';
import mixpanel from 'mixpanel-browser';
import { act, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { usePathApi } from './awsS3';
import { useAxios } from './axiosApi';
import { onError } from './errorLib';
import { loadJsonFile } from './jsonLib';
import { useTextureAuth } from './textureAuthContext';
import { useAuth } from './useAuth';
import { USER_TILING_INCLUDED } from './users';

import { tilingActive } from './shared/business';
import { splitExt } from './shared/formatLib';
import { excludedStatePreviewMat, getBrdfConfig, getGalleryState, ScanType, setGalleryState, setBrdfConfig, mergeState, TEXTURE_FORMAT_DEFAULTS } from './shared/metadataLib';

export const chartRename = new Map(Object.entries({
  A6: 'XS',
  A5: 'S',
  A4: 'M',
  A3: 'L',
  A2: 'XL',
  A1: '2XL',
  A0: '3XL',
  B0: '4XL',
}));

export const DEFAULT_FILTERS = {
  favourite: false,
  unlocked: false,
  locked: false,
  tilingAccepted: false,
  tilingNotAccepted: false,
  qa: false,
  noQa: false,
  showStitchInputs: false,
  name: '',
  description: '',
  tags: [],
  tagsExclude: [],
};

export const IccProfileAction = Object.freeze({
  ASSIGN: 'assign',
  PRESERVE: 'preserve',
});

export const IccProfileNamed = Object.freeze({
  SRGB: 'sRGB',
});

export const ICC_PROFILE_DEFAULT = {
  action: IccProfileAction.ASSIGN,
  assignSystem: IccProfileNamed.SRGB,
}

export function getAutoTags(mat, userTilingIncluded) {
  let internalAutoTags = [];
  if (mat?.['tags.auto']) {
    internalAutoTags = mat['tags.auto'].slice();
  }

  // Create a Map with the tag as the key and autoTagType as the value. We prefix the autoTagType with a
  // number to define the sort order for the auto tags.
  let allTags = internalAutoTags.map((t) => {
    if (t.match(/\d+k$/)) {
      // Convert resolution tags ending in k to uppercase K to match convention in other graphics tools
      return [t.toUpperCase(), '0_resolution'];
    } else if (chartRename.has(t)) {
      return [chartRename.get(t), '1_frameSize'];
    } else {
      return [t, '2_other'];
    }
  });

  const allTagsMap = new Map(allTags);

  if (mat?.tiledUserPath && tilingActive(mat?.tilingIncluded, userTilingIncluded) && !mat?.tilingParams?.skipTiling) {
    allTagsMap.set('Tiled', '2_other');
  }

  return allTagsMap;
}

export function getSelectedMats(materials, selected) {
  const selectedSet = new Set(selected);
  return materials.filter(mat => selectedSet.has(mat.materialId));
}

export function makePseudoMat(tagsAuto, tagsUser, tilingIncluded, tiledUserPath, tilingParams) {
  // Return a pseudo material object with the fields needed for collectTags(). This is used to call
  // collectTags() from circumstances where a full material object is not available.
  const mat = {
    'tags.auto': tagsAuto,
    'tags.user': tagsUser,
    tilingIncluded,
    tiledUserPath,
    tilingParams,
  };
  return mat;
}

export function collectTags(mats, userTilingIncluded, userList=null) {
  // Must supply one of mats or userList.
  //  mats: A list of materials from which to collect tags.
  //  userList: A list of tags directly supplied.
  var tags = {};
  if (userList) {
    userList.forEach(t => {
      if (tags[t]) {
        tags[t].count += 1;
      } else {
        tags[t] = { 'tagType': 'user', 'count': 1 };
      }
    });
  } else if (mats) {
    mats.forEach(m => {
      getAutoTags(m, userTilingIncluded).forEach((autoTagType, tag) => {
        if (tags[tag]) {
          tags[tag].count += 1;
        } else {
          tags[tag] = { 'tagType': 'auto', 'count': 1, autoTagType };
        }
      });
      if (m['tags.user']) {
        m['tags.user'].forEach(t => {
          if (tags[t]) {
            tags[t].count += 1;
          } else {
            tags[t] = { 'tagType': 'user', 'count': 1 };
          }
        });
      }
    });
  }

  function compareTags(t1, t2) {
    // Primary: User -> Auto
    if (t1.tagType == 'user' && t2.tagType == 'auto') {
      return -1;
    } else if (t1.tagType == 'auto' && t2.tagType == 'user') {
      return 1;
    }
    // Secondary: autoTagType
    if (t1?.autoTagType && t2?.autoTagType) {
      if (t1?.autoTagType < t2?.autoTagType) {
        return -1;
      } else if (t1?.autoTagType > t2?.autoTagType) {
        return 1;
      }
      if (t1?.autoTagType === '1_frameSize' && t1?.autoTagType === t2?.autoTagType) {
        const t1i = Array.from(chartRename.values()).indexOf(t1.label);
        const t2i = Array.from(chartRename.values()).indexOf(t2.label);
        if (t1i < t2i) {
          return -1;
        } else if (t1i > t2i) {
          return 1;
        }
      }
    }

    // Secondary (disabled): count
    // if (t1.count > t2.count) {
    //   return -1;
    // } else if (t1.count < t2.count) {
    //   return 1;
    // }

    // Tertiary : alphabetical case-insensitive
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return collator.compare(t1.label, t2.label);
  }

  var tagsArray = [];
  for (const [key, value] of Object.entries(tags)) {
    tagsArray.push({'label': key, ...value});
  }
  tagsArray.sort(compareTags);

  return tagsArray;
}

export function sortMaterials(mats, key, descending=false) {
  function compareByKey(a, b) {
    const s = descending ? -1 : 1;
    return (a[key] < b[key]) ? -s : ((a[key] > b[key]) ? s : 0);
  }
  return mats.sort(compareByKey);
}

// Return true if the elements in the two arrays of materials contain
// the same material IDs in the same order.
export function materialArraysEqual(array1, array2) {
  var equal = true;
  if (array1.length != array2.length) {
    equal = false;
  } else {
    for (var i = 0; i < array1.length; i++) {
      if (array1[i].materialId != array2[i].materialId) {
        equal = false;
        break;
      }
    }
  }
  return equal;
}

export function isMaterialProcessing(material) {
  return ['queued', 'processing'].includes(material.status);
}

export function isMaterialFailed(material) {
  return (
    material.status === 'processed' &&
    (material.qaStatus === 'qa' || !material.success)
  );
}

export const DEFAULT_DAYS_SINCE = 1;
// Cached fetch of materials.
// Handles more parameters than the uncached getMaterials() futher below.
export function useMaterials(userId, materialId, daysSince, allPending, qaDisplay, qaNeeded, refreshInterval) {
  const { user, isAuthenticated } = useAuth();
  const { textureToken } = useTextureAuth();

  const { apiGet, apiDelete, apiPut } = useAxios();

  // If userId is not specified, use the current user.
  const userIdQuery = userId ?? user?.sub;

  let fromDate = null;
  if (daysSince) {
    fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - daysSince);
    // Round date down to nearest day to avoid unnecessary cache misses
    fromDate.setHours(0, 0, 0, 0);
  }

  const params = qaNeeded ? { qaNeeded } : { userId: userIdQuery, materialId, fromDate, allPending, showExpired: qaDisplay };
  const { data: materialItems, error, mutate } = useSWR(
    (textureToken || (isAuthenticated && userIdQuery)) ?
      ['/materials', { params }, textureToken ? 'texture' : 'auth0']
      : null,
    async ([path, config]) => {
      const response = await apiGet(path, config);
      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message);
      }
      return response.data?.data;
    },
    { refreshInterval },
  );

  // Normally useMemo wouldn't pick up changes in nested objects, but useSWR uses deep comparison and changes
  // the parent object reference when it picks up any changes, so these useMemo depdendencies trigger
  // correctly.

  const materials = useMemo(() => (
    materialItems ? materialItems.filter(m => !m.deleted) : null
  ), [materialItems]);

  const folders = useMemo(() => (
    materials ? [...new Set(materials.map(m => m.folder ?? ''))].sort() : null
  ), [materials]);

  const stitchedInputMids = useMemo(() => (
    materials ? materials.filter(m => m.stitched && m.stitchInputs).reduce(
      (acc, m) => [...acc, ...m.stitchInputs.flat()],
    []) : null
  ), [materials]);

  // We use special handling for tags to ensure the object reference only changes when the contents change.
  // useMemo doesn't work because materials can change without any change to the output of collectTags().

  const [tags, setTags] = useState([]);

  if (materials) {
    const newTags = collectTags(materials, USER_TILING_INCLUDED);
    if (!dequal(newTags, tags)) {
      setTags(newTags);
    }
  }

  // We need to memoize these material subsets, because otherwise downstream hooks that depend on these
  // outputs will rerun on every render.
  const sortedMaterials = useMemo(() => (
    materials ? sortMaterials(materials, 'createdAt', true) : null
  ), [materials]);

  const displayMaterials = useMemo(() => (
    sortedMaterials ? sortedMaterials.filter(m => (
      (m.qaStatus === 'qa' && qaDisplay) ||
      (m.qaStatus === 'released' && (
        (m.status === 'processed') ||
        (m.texBaking && m.texBaking.status === 'processing')
      ))
    )).map(m => ({
      ...m,
      visible: true,
      isStitchInput: stitchedInputMids.includes(m.materialId),
    })) : null
  ), [qaDisplay, sortedMaterials, stitchedInputMids]);

  const processingMaterials = useMemo(() => (
    sortedMaterials ? sortedMaterials.filter(m => isMaterialProcessing(m)) : null
  ), [sortedMaterials]);

  const processingMaterialsCount = processingMaterials ? processingMaterials.length : null;

  const failedMaterials = useMemo(() => (
    sortedMaterials ? sortedMaterials.filter(m => isMaterialFailed(m)) : null
  ), [sortedMaterials]);

  const failedMaterialsCount = processingMaterials ? failedMaterials.length : null;

  const deleteMaterial = useCallback(async function (materialId) {
    var response = {};
    try {
      response = await apiDelete('/materials', {
        params: { userId: userIdQuery, materialId }
      });
    } catch (e) {
      onError(e);
      return false;
    }
    const editedMaterials = materials.map((item) => (
      item.materialId === materialId ? { ...item, deleted: true } : item
    ));
    mutate(editedMaterials);
    return response.data?.status === 'success';
  }, [apiDelete, materials, mutate, userIdQuery]);

  const putFields = useCallback(async function (materialsChanged) {
    let response = {};

    // Work around for multiple rapid updates:
    // (adapted from https://github.com/vercel/swr/issues/2701#issuecomment-1769328493)
    //
    // If putFields is called multiple times in quick succession A then B, then the optimistic update callback
    // for B may receive stale data (without the update made in A). This means the displayed state can be
    // confusing until all of the updates are complete on the API side *and* SWR revalidates the cache.
    //
    // A work around is to use the bound data from useSWR to update the optimistic cache instead of the data
    // received in the callback function parameter. It's also important that putFields is called *without*
    // await, as calling it with await will make the updates happen sequentially instead of in parallel making
    // the whole update slower and this also confuses the SWR revalidation logic causing multiple
    // revalidations.
    //
    // With this work around there is still a delay before the updates are displayed, but at least the
    // displayed state evolves in a consistent way.
    //
    // If multiple items are being changed in a single user operation (such as batch operations on selected
    // materials to change the tags or move to a different folder), then to work around this delay we collate
    // the changes into a single mutate call. SWR seems to behave better when the fetcher is operating on a
    // symmetric set of data in the useSWR hook and the corresponding bound mutate call. So with the collation
    // it means the mutate call is operating on the set of loaded materials, which is symmetric with the
    // bound useSWR list of materials. (Previously the data was asymmetric because the useSWR hook was
    // fetching the list of materials while the bound mutate was being called on one material at a time).

    const updateFieldCache = () => {
      const materialChangesDb = {};
      for (const [materialId, fields] of Object.entries(materialsChanged)) {
        const dbFields = {};
        Object.keys(fields).forEach(key => {
          let dbKey;
          if (key === 'description') {
            dbKey = 'text';
          } else if (key === 'tags') {
            dbKey = 'tags.user';
          } else {
            dbKey = key;
          }
          dbFields[dbKey] = fields[key];
        });
        materialChangesDb[materialId] = dbFields;
      }
      const updatedMaterials = materialItems.map((item) => (
        Object.keys(materialChangesDb).includes(item.materialId) ?
        { ...item, ...materialChangesDb[item.materialId] } :
        item
      ));
      return updatedMaterials;
    };

    const updateFieldAPI = async () => {
      try {
        await Promise.all(
          Object.entries(materialsChanged).map(async ([materialId, fields]) => {
            const response = await apiPut('/materials', fields, {
              params: { userId: userIdQuery, materialId }
            });
            const { status, data } = response.data;
            if (status !== 'success') {
              throw new Error(data);
            }
          })
        );
      } catch (e) {
        onError(e);
      }
    };

    let success;
    try {
      mutate(updateFieldAPI, { optimisticData: updateFieldCache, populateCache: false });
      success = true;
    } catch (e) {
      success = false;
    }

    return success;
  }, [apiPut, materialItems, mutate, userIdQuery]);

  // For some material field changes, the materialsPut API cannot be used to directly manipulate the values.
  // For example, to unlock a material the billingPut API needs to be called. The reloadMaterial function can
  // be used to update the materials cache after an indirect change to a material. The caller can supply the
  // fields and values that they believe to have changed, and mutate will do an optimistic local update and
  // then fetch the full materials list from the DB. If there is a mis-match, then the updates will rollback
  // in the local cache.
  const reloadMaterial = useCallback(async function (materialId, fields) {
    let updateFieldCache;

    if (fields) {
      updateFieldCache = materials => (
        materials.map((item) => (
          item.materialId === materialId ? { ...item, ...fields } : item
        ))
      );
    } else {
      updateFieldCache = materials => materials;
    }

    let success;
    try {
      mutate(updateFieldCache, { optimisticData: updateFieldCache })
      success = true;
    } catch (e) {
      success = false;
    }

    return success;
  }, [mutate]);

  return {
    materials,
    sortedMaterials,
    displayMaterials,
    processingMaterials,
    processingMaterialsCount,
    failedMaterialsCount,
    folders,
    tags,
    deleteMaterial,
    putFields,
    reloadMaterial,
    error,
    mutate,
  }
}

const THUMBNAIL_WILDCARD = 'gallery/*/biv_gallery/images/0.jpg';

export function useThumbnailUrls (userId, materials, componentKey) {
  const { user, isAuthenticated } = useAuth();
  const { textureToken } = useTextureAuth();

  const { apiPost } = useAxios();

  // If userId is not specified, use the current user.
  const userIdQuery = userId ?? user?.sub;

  // Force a separate SWR cache for each component that uses this hook, by including the componentKey in the
  // cache key. The componentKey should be a string that is unique to each component, for example the name of
  // the calling component. This allows the hook to mutate the thumbnail URLs for a specific component without
  // affecting other components that use the same hook.

  // FIXME: This design spawns many API calls to /path which are relatively slow and expensive. It could be
  // optimised to reduce /path calls, for example by using a single shared and merged prevSavedAt state for
  // all components that use this hook.

  // Special case: for single material, use direct path instead of wildcard. Using the specific materialId
  // allows the path API to grant access the path even when a texture token is being used that only allows
  // access to a single material.
  const useSinglePath = materials && materials.length === 1;
  const singleMaterialPath = useSinglePath ? `gallery/${materials[0].materialId}/biv_gallery/images/0.jpg` : null;
  const fileUserPaths = useSinglePath ? { thumbnail: singleMaterialPath } : null;

  const { data, mutate } = useSWR(
    (textureToken || (isAuthenticated && userIdQuery)) && materials && materials.length > 0 ?
      useSinglePath ?
        ['/path', { userId: userIdQuery, mode: 'getObject', fileUserPaths }, componentKey, textureToken ? 'texture' : 'auth0']
        :
        ['/path', { userId: userIdQuery, mode: 'getObject', wildcard: THUMBNAIL_WILDCARD }, componentKey, textureToken ? 'texture' : 'auth0']
      : null,
    async ([path, data, config]) => {
      const response = await apiPost(path, data, config);
      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message);
      }
      return response.data?.data;
    },
    // Updating the thumbnail URLs makes the UI flash, so we only revalidate when the URL signature is about
    // to expire.
    {
      revalidateOnFocus : false,
      refreshInterval: (data) => {
        const { expiresAt } = data || { };
        if (!data || !expiresAt) return 10_000;
        const timeRemaining = expiresAt - Date.now();
        const fiveMinutes = 5*60*1000;
        return timeRemaining > fiveMinutes ? timeRemaining -  fiveMinutes : 15_000;
      }
    },
  );

  const thumbnailUrls = useMemo(() => {
    if (!materials || !data) return {};

    if (useSinglePath) {
      // For single material, use the direct URL from fileUserPaths response
      const { urls } = data;
      if (urls?.thumbnail) {
        return { [materials[0].materialId]: urls.thumbnail };
      }
      return {};
    } else {
      // For multiple materials, use wildcard replacement logic
      const { wildcardUrl } = data;
      return materials.reduce((acc, m) => {
        acc[m.materialId] = wildcardUrl.replace(
          encodeURIComponent('gallery/*/biv_gallery/images/0.jpg'),
          encodeURIComponent(`gallery/${m.materialId}/biv_gallery/images/0.jpg`)
        );
        return acc;
      }, {});
    }
  }, [materials, data, useSinglePath]);

  const [prevSavedAt, setPrevSavedAt] = useState(null);

  const thumbnailsSavedAt = materials ? materials.map(m => [m.materialId, m.thumbnailSavedAt]) : null;
  const thumbnailsChanged = thumbnailsSavedAt && !dequal(thumbnailsSavedAt, prevSavedAt);

  // FIXME: This triggers a reload of all visible thumbnails even if only one has changed. Ideally we would
  // only update any changed thumbnailUrls, while still correctly handling expiry for unchanged thumbnailUrls.
  if (thumbnailsChanged) {
    mutate();
    setPrevSavedAt(thumbnailsSavedAt);
  }

  return { thumbnailUrls };
};

// Direct fetch of materials, without using SWR cache.
export function useMaterialsApi() {
  const { apiGet, apiPut, apiPost } = useAxios();
  const { getPresignedUrls } = usePathApi();

  return{

    getBillingCost: async (userId, materialIds, isServiced) => {
      const response = await apiPost('/billing', { userId, materialIds, isServiced });
      const { status, data } = response.data;
      if (status === 'success') {
        return data;
      } else {
        console.log(`ERROR: getBillingCost failed: ${userId} ${materialIds} ${isServiced}`);
        return null;
      }
    },

    getStitchedInputs: useCallback(async (userId, materialId) => {
      const response = await apiGet('/stitching', {
        params: { userId, materialId }
      });
      const { status, data } = response.data;
      if (status === 'success') {
        return data;
      } else {
        console.log(`ERROR: getStitching failed: ${userId} ${materialId}`);
        return null;
      }
    }, [apiGet]),

    stitchMaterial: async (userId, materialIds, name) => {
      // Define a new stitched material
      const response = await apiPost(
        '/stitching',
        { materialIds, name, arrange: 'full' },
        { params: { userId } }
      );
      const { status, data } = response.data;
      if (status === 'success') {
        return data['materialId'];
      } else {
        return false; // TODO: Better error handling
      }
    },

    getMaterial: async (userId, materialId, showExpired) => {
      const response = await apiGet('/materials', {
        params: { userId, materialId, showExpired }
      });
      const { status, data } = response.data;
      if (status === 'success') {
        return data;
      } else {
        console.log(`ERROR: getMaterial failed: ${userId} ${materialId}`);
        return null;
      }
    },

    getMaterials: async (userId, showExpired = false) => {
      // Exception handling is performed by caller
      const params = {
        userId: userId,
      };

      // Add showExpired parameter if true
      if (showExpired) {
        params.showExpired = showExpired;
      }

      const response = await apiGet('/materials', {
        params,
      });
      const { status, data } = response.data;
      if (status === 'success') {
        return data;
      } else {
        console.log('ERROR: getMaterials failed:', userId);
        return null;
      }
    },

    putFavourite: async (userId, materialId, value) => {
      var response = {};
      try {
        response = await apiPut('/materials', { favourite: value }, {
          params: { userId, materialId },
        });
      } catch (e) {
        onError(e);
      }
      return (response.data?.status === 'success');
    },

    putTilingAccepted: async (userId, materialId, value) => {
      var response = {};
      try {
        response = await apiPut('/materials', { tilingAccepted: value }, {
          params: { userId, materialId },
        });
      } catch (e) {
        onError(e);
      }
      return (response.data?.status === 'success');
    },

    putFolder: async (userId, materialId, value) => {
      var response = {};
      try {
        response = await apiPut('/materials', { folder: value }, {
          params: { userId, materialId },
        });
      } catch (e) {
        onError(e);
      }
      return (response.data?.status === 'success');
    },

    putUserTags: async (userId, materialId, tags) => {
      var response = {};
      try {
        response = await apiPut('/materials', { tags }, {
          params: { userId, materialId },
        });
      } catch (e) {
        onError(e);
      }
      return (response.data?.status === 'success');
    },

    putTilingIncluded: async (userId, materialId, value) => {
      var response = {};
      try {
        response = await apiPut('/materials', { tilingIncluded: value }, {
          params: { userId, materialId },
        });
      } catch (e) {
        onError(e);
      }
      return (response.data?.status === 'success');
    },

    putTilingParams: async (userId, materialId, value) => {
      var response = {};
      try {
        response = await apiPut('/materials', { tilingParams: value }, {
          params: { userId, materialId },
        });
      } catch (e) {
        onError(e);
      }
      return (response.data?.status == 'success');
    },

    putTextureFormat: async (userId, materialId, value) => {
      var response = {};
      try {
        response = await apiPut('/materials', { textureFormat: value }, {
          params: { userId, materialId },
        });
      } catch (e) {
        onError(e);
      }
      return (response.data?.status == 'success');
    },

    getThumbnailUrl: async (userId, materialId) => {
      const fileUserPaths = {'thumb': `gallery/${materialId}/biv_gallery/images/0.jpg`};
      const urls = await getPresignedUrls(fileUserPaths, userId);
      return urls['thumb'];
    },

    getReportPhotoThumbnailUrls: async (material) => {
      const fileUserPaths = {};
      for (const photoFilename of material.photos) {
        const thumbFilname = splitExt(photoFilename)[0] + '-thumb.jpg';
        fileUserPaths[photoFilename] = `${material.triageUserPath}/thumbs/${thumbFilname}`;
      }
      const urls = await getPresignedUrls(fileUserPaths, material.userId);
      return urls;
    },

    getReportPhotoPreviewUrls: async (material) => {
      const fileUserPaths = {};
      for (const photoFilename of material.photos) {
        const thumbFilname = splitExt(photoFilename)[0] + '.jpg';
        fileUserPaths[photoFilename] = `${material.triageUserPath}/previews/${thumbFilname}`;
      }
      const urls = await getPresignedUrls(fileUserPaths, material.userId);
      return urls;
    },

    // TODO: This static image will be replaced by calling a plotting library with the raw pose data.
    getReportPlotUrls: async (material) => {
      const fileUserPaths = {
        'poses': `${material.triageUserPath}/poses.png`,
        'dartboard': `${material.triageUserPath}/dartboard.png`,
      };
      const urls = await getPresignedUrls(fileUserPaths, material.userId);
      return urls;
    },

    getReport: async (material) => {
      const fileUserPaths = {
        'reportData': `${material.triageUserPath}/triage-report.json`,
        'reportText': `${material.triageUserPath}/triage-report.log`,
      };
      const urls = await getPresignedUrls(fileUserPaths, material.userId);
      let reports = {};
      for (const [key, value] of Object.entries(urls)) {
        if (value) {
          const response = await axios.get(value);
          reports[key] = response.data;
        } else {
          reports[key] = null;
        }
      }
      return [reports['reportData'], reports['reportText']];
    },

    // Return the mask image paths and metadata for the colour masks for a material, or null if not present
    getColourMaskUrls: async (userId, materialId) => {
      var metadataPath = {'metadataUrl': `materials/${materialId}/r000/${materialId}__r000_tt/colour_masks/masks-metadata.json`};
      const { metadataUrl } = await getPresignedUrls(metadataPath, userId);
      if (!metadataUrl) {
        return null;
      }
      const metadata = await loadJsonFile(metadataUrl);
      if (!metadata) {
        return null;
      }

      var fileUserPaths = {};
      for (var n of Object.keys(metadata)) {
        for (var i = 0; i < n; i++) {
          fileUserPaths[`${n}_${i}`] = `materials/${materialId}/r000/${materialId}__r000_tt/colour_masks/colour_mask_${n}_${i}.png`;
        }
      }
      const urls = await getPresignedUrls(fileUserPaths, userId);
      return { urls, metadata };
    },

    // Start baking processing for a scan, if needed.
    // Returns true if baking is in progress at the end of the call.
    processBaking: async (material) => {
      const { userId, materialId, materialUserPath, texturesUserPath, tiledUserPath } = material;

      if (!texturesUserPath && !tiledUserPath) {
        return false; // No texture outputs; nothing to bake
      }
      console.debug(`Starting baking processing for materialId ${materialId} for userId ${userId} using materialUserPath ${materialUserPath}`);

      const geom = 'displacement';
      const target = ['textures', 'tiled'];
      const action = 'bake';

      const response = await apiPost('/process',
        { materialUserPath, target, geom, action },
        {
          params: { userId, materialId },
        },
      );
      const { status, data } = response.data;

      if (status === 'success') {
        return data['bakingInProgress'];
      } else {
        return false; // TODO: Better error handling
      }
    },

    // Start stitching processing for a scan
    processStitch: async (material) => {
      const { userId, materialId, materialUserPath } = material;
      if (!materialUserPath) {
        return false; // No material to stitch
      }
      const geom = 'displacement';
      const target = ['shimmer', 'textures', 'tiled'];
      const action = 'stitching';
      const response = await apiPost(
        '/process',
        { materialUserPath, target, geom, action },
        { params: { userId, materialId } }
      );
      const { status, data } = response.data;
      return (status === 'success');
    },
  }
}

export function useMaterialStateApi(material, showAlert, previewMaterial) {
  const { apiPut } = useAxios();
  const { getPresignedUrls } = usePathApi();

  // Call the materials.PUT API with the materialSet on the given material.
  async function putMaterial(userId, materialId, materialSet, previewMat=undefined, alertOnSuccess=undefined) {
    var response = {};
    try {
      response = await apiPut('/materials', { materialSet, previewMat }, {
        params: { userId, materialId },
      });
    } catch (e) {
      onError(e);
    }
    const { status, data } = response.data ?? {};

    if (previewMat) {
      // For preview materials, don't update the materialUserPath, and don't show an alert
      return (status === 'success');
    }

    if (status === 'success') {
      const { materialUserPath, materialPath } = data;
      materialSet.self_location = materialPath;
      materialSet.self_user_path = materialUserPath;
      material.materialUserPath = materialUserPath;
      if (alertOnSuccess) {
        showAlert({ status: 'success', message: 'Shimmer settings saved' });
      }
      return true;
    } else {
      showAlert({ status: 'error', message: 'Error saving Shimmer settings' });
      return false;
    }
  }
  return {
    putSharedState: async (userId, materialId, materialSet) => {
      mixpanel.track('Save Material', { 'Material ID': material.materialId, 'Material Name': material.name });

      // Save the primary material
      var result = await putMaterial(userId, materialId, materialSet, false, true);

      // If there is a tiled preview material, also update it with selected saved settings
      if (previewMaterial) {
        const { materialUserPath } = previewMaterial;
        const fileUserPaths = { 'ms': materialUserPath };
        const url = await getPresignedUrls(fileUserPaths, userId);
        const filename = url['ms'];
        const prevMatSet = await loadJsonFile(filename);
        const state = getGalleryState(materialSet);
        const prevState = getGalleryState(prevMatSet);
        mergeState(state, prevState, excludedStatePreviewMat);
        const previewResult = await putMaterial(userId, previewMaterial.materialId, prevMatSet, true);
        result &= previewResult;
      }

      return result;
    },
    putState: async (userId, materialId, isTiled, galleryState, brdfState) => {
      mixpanel.track('Save state', { 'Material ID': material.materialId, 'Material Name': material.name, 'isTiled': isTiled });
      var result = false;

      const { materialUserPath } = isTiled ? previewMaterial : material;
      const fileUserPaths = { 'ms': materialUserPath };
      const url = await getPresignedUrls(fileUserPaths, userId);
      const filename = url['ms'];
      const matSet = await loadJsonFile(filename);
      if (galleryState) {
        setGalleryState(matSet, galleryState);
      }
      if (brdfState) {
        setBrdfConfig(matSet, brdfState);
      }

      const mid = isTiled ? previewMaterial.materialId : materialId;
      result = await putMaterial(userId, mid, matSet, isTiled);
      return result;
    },
  }
}

export function useBakingApi() {
  const { getMaterial, processBaking } = useMaterialsApi();

  return {
    checkBakingStatus: async (userId, materialId, qaDisplay) => {
      // Checks the baking status of a material.  If baking is needed, initiates baking.
      // Return true if baking is up to date, and false if baking is currently being performed.
      if (userId && materialId) {
        // FIXME: Receive material from the caller instead of fetching it again here.
        const material = await getMaterial(userId, materialId, qaDisplay);

        if (!material.texturesUserPath && !material.tiledUserPath) {
          return true; // Only process baking if the material has texture outputs
        }

        if (!material.unlocked) {
          return true; // Only perform texture baking for unlocked materials
        }

        // FIXME: Trigger a baking check during material unlock from inside the API, so that we can remove our
        // complicated baking state polling from the client download windows. Instead rely on useMaterials in
        // the Gallery to refresh our material cache and simply read the baking state from the material
        // object. We would still need a server call to check the baking status when actually commencing a
        // download, in case the user edits the material from another window while the download window is
        // open.
        const bakingUnderway = await processBaking(material);
        return !bakingUnderway;
      } else {
        return false;
      }
    },
  }
}

export function useTilingApi() {
  const { apiPost } = useAxios();
  const { putTilingParams } = useMaterialsApi();

  return {
    // Start tiling processing for a scan.
    processTiling: async (material, tilingParams, textureFormat) => {
      const { userId, materialId, materialUserPath, tiledUserPath } = material;

      if (!tiledUserPath) {
        return false; // No tiled outputs; nothing to tile
      }

      // Don't set null values to corners or control points; remove them instead
      const tpCopy = { ...tilingParams };
      if (tpCopy.hasOwnProperty('corners') && !tpCopy.corners) {
        delete tpCopy.corners;
      }
      if (tpCopy.hasOwnProperty('controlPoints') && !tpCopy.controlPoints) {
        delete tpCopy.controlPoints;
      }

      // Write the tiling params to the database
      console.debug('processTiling(): sending tiling params with',
        tpCopy.controlPoints ? Math.floor(tpCopy.controlPoints.length / 2) : 0, 'control point pairs');
      const res = await putTilingParams(userId, materialId, tpCopy);
      if (!res) {
        console.log('processTiling(): putTilingParams failed:', res)
        return false;
      }

      // Start processing
      console.log(`Starting tiling processing for materialId ${materialId} for userId ${userId} using materialUserPath ${materialUserPath}`);

      const geom = 'displacement';
      // If the texture format has changed, then we need to re-process the untiled outputs (textures target)
      // as well as the tiled outputs, otherwise we can just re-process the tiled outputs.
      //
      // This assumes the material still contains the previously processed textureFormat values.
      const prevTextureFormat = { ...TEXTURE_FORMAT_DEFAULTS, ...(material.textureFormat || {}) };
      const nextTextureFormat = { ...TEXTURE_FORMAT_DEFAULTS, ...textureFormat };
      const textureFormatChanged = ['pixelFormat', 'normalFormat', 'diffuseType'].some(
        (key) => nextTextureFormat[key] !== prevTextureFormat[key]
      );
      const target = textureFormatChanged ? ['textures', 'tiled'] : ['tiled'];
      const action = 'tiling';

      const response = await apiPost('/process',
      { materialUserPath, target, geom, action },
      {
        params: { userId, materialId },
      }
      );
      const { status, data } = response.data;

      return (status === 'success');
    },
  }
}

export function previewMatFromMat(material) {
  if (material?.tiledUserPath) {
    var ms_parts = material.tiledUserPath.split('/');
    ms_parts.pop();
    ms_parts.push('preview/gallery/biv_gallery/material-set.json');
    var newMat = {...material};
    newMat.materialUserPath = ms_parts.join('/');
    return newMat;
  } else {
    return null;
  }
}

export function useMetadataApi() {
  const { getPresignedUrls } = usePathApi();
  return {
    fetchGalleryState: async (mat) => {
      // Fetch the material set for the given material item and return the gallery state and BRDF config
      const { materialUserPath, userId } = mat;
      try {
        const url = await getPresignedUrls({ 'ms': materialUserPath }, userId);
        const filename = url['ms'];
        const matSet = await loadJsonFile(filename);
        return {
          state: getGalleryState(matSet),
          brdfConfig: getBrdfConfig(matSet),
        };
      } catch (e) {
        console.warn('Failed to load material set for material:', materialUserPath);
        return null;
      }
    },
    diffBrdfConfig: async (mats, keys) => {
      // For the given BRDF config keys, check whether the config values match for all given material objects
      const { userId } = mats[0];

      try {
        // Get material set paths for each material
        const paths = Object.fromEntries(mats.map((mat, i) => [mat.materialId, mat.materialUserPath, i]));
        const urls = await getPresignedUrls(paths, userId);

        // Fetch the material set for all materials in parallel
        var promises = {};
        Object.entries(urls).map(async ([mid, filename]) => {
          promises[mid] = loadJsonFile(filename);
        });
        var matSets = {};
        for (var mid of Object.keys(promises)) {
          matSets[mid] = await promises[mid];
        }

        // Compare the given keys for each material
        var diffs = [];
        const config0 = getBrdfConfig(Object.values(matSets)[0]);
        for (var [mid, ms] of Object.entries(matSets)) {
          const config1 = getBrdfConfig(ms);
          var diffKeys = [];
          for (var key of keys) {
            if (config0[key] !== config1[key]) {
              diffKeys.push({ key, value0: config0[key], value1: config1[key] });
            }
          }
          if (diffKeys.length > 0) {
            diffs.push({ mid, diffKeys });
          }
        }
        return { success: true, data: { diffs } };
      } catch (e) {
        return { success: false, data: {} };
      }
    },
  }
}

export function diffTextureMetadata(materials, keys, diffTiled=false) {
  const diffs = [];
  const prefix = diffTiled ? 'tiled' : 'untiled';
  const config0 = materials[0].textureMetadata[prefix];
  for (const material of materials) {
    const config1 = material.textureMetadata[prefix];
    let diffKeys = [];
    for (const key of keys) {
      if (!dequal(config0[key], config1[key])) {
        diffKeys.push({ key, value0: config0[key], value1: config1[key] });
      }
    }
    if (diffKeys.length > 0) {
      diffs.push({ mid: material.materialId, diffKeys, prefix });
    }
  }
  return diffs;
}
